import { format, isValid } from 'date-fns'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { InfoIcon } from '@/components/icons'
import { BILLING_CYCLES, CREATIVE_TIERS } from '../config'
import { SUBSCRIPTION_CREATIVE } from '../constants'
import CreativesUsageRow from '../CreativesUsageRow/CreativesUsageRow'
import NextPaymentStatBlock from './NextPaymentStatBlock'
import StatCell from './StatCell'

const BILLING_CYCLE_KEYS = Object.keys(BILLING_CYCLES)
const CREATIVE_TIER_KEYS = Object.keys(CREATIVE_TIERS)

const PILL_CLASS = 'tw:px-2.5 tw:py-0.5 tw:rounded-2xl tw:bg-[#263E57] tw:text-sm tw:text-white'

const formatAmount = (amount) =>
    typeof amount === 'number' && Number.isFinite(amount)
        ? `$${amount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`
        : '—'

const formatDate = (value) => {
    if (!value) return '—'
    const d = new Date(value)
    if (!isValid(d)) return '—'
    return format(d, 'dd/MM/yyyy')
}

const monthsSince = (value) => {
    if (!value) return ''
    const start = new Date(value)
    if (Number.isNaN(start.getTime())) return ''
    const now = new Date()
    const months = (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth())
    if (months < 1) return 'Less than a month'
    if (months < 12) return `${months} month${months === 1 ? '' : 's'}`
    const years = Math.floor(months / 12)
    return `${years} year${years === 1 ? '' : 's'}`
}

const daysUntil = (value) => {
    if (!value) return ''
    const end = new Date(value)
    if (Number.isNaN(end.getTime())) return ''
    const ms = end.getTime() - Date.now()
    const days = Math.ceil(ms / (1000 * 60 * 60 * 24))
    if (days < 0) return 'Ended'
    if (days === 0) return 'Resets today'
    if (days === 1) return 'Resets tomorrow'
    return `Resets in ${days} days`
}

const StatusBadges = ({ cycleLabel, tierLabel, showTier, expired = false }) => {
    if (!cycleLabel && !(showTier && tierLabel) && !expired) return null
    return (
        <div className="tw:flex tw:items-center tw:gap-2 tw:flex-wrap">
            {cycleLabel && <span className={PILL_CLASS}>{cycleLabel}</span>}
            {showTier && tierLabel && <span className={PILL_CLASS}>{tierLabel}</span>}
            {expired && <span className={PILL_CLASS}>Expired</span>}
        </div>
    )
}

const CurrentPlanCard = ({
    planDef,
    subscription = null,
    endedSubscription = null,
    unsettledSubscription = null,
    onRetryPayment,
    retrying = false,
    bankActionUrl = null,
    endedPlan = null,
    plan = null,
    cycle,
    tier,
    creativeFrequency,
    onCycleChange,
    onTierChange,
    onCreativeFrequencyChange,
    onAdd,
    pending = false,
    extendFrequency = 'monthly',
    onExtendFrequencyChange,
    extendTier,
    onExtendTierChange,
    onExtend,
    extending = false,
    quota = null,
    onAutoRenewChange,
    autoRenewPending = false,
}) => {
    if (!planDef) return null
    const Icon = planDef.Icon

    const isCreative = planDef.id === SUBSCRIPTION_CREATIVE
    const isActive = !!subscription
    // A subscription that exists and is owed money sits between live and finished: the customer can
    // settle it, and must not be offered a second subscription as if they had never had one.
    const isUnsettled = !isActive && !!unsettledSubscription
    const isEnded = !isActive && !isUnsettled && !!endedSubscription
    const displaySub = subscription ?? unsettledSubscription ?? endedSubscription

    // Pricing source: live sub when active, ended sub when expired (so the pill row
    // reflects the plan the user previously had), otherwise the catalog plan tied
    // to the current dropdown selection.
    const pricingSource = subscription ?? endedPlan ?? plan
    const perMonth = pricingSource?.amountPerMonth ?? null
    const yearTotal = typeof perMonth === 'number' && Number.isFinite(perMonth) ? perMonth * 12 : null
    const adsCount = isCreative ? (pricingSource?.creativesQuantity ?? null) : null

    const priceParts = [`${formatAmount(perMonth)}/month`, `${formatAmount(yearTotal)}/year`]
    if (isCreative && adsCount != null) priceParts.push(`${adsCount} ads included`)

    const badgePlan = isActive ? plan : endedPlan
    const badgeCycleLabel = displaySub ? (BILLING_CYCLES[displaySub.frequency]?.label ?? displaySub.frequency) : null
    const badgeTierLabel = badgePlan?.tier ? (CREATIVE_TIERS[badgePlan.tier]?.label ?? badgePlan.tier) : null

    const inactiveCycle = isCreative ? creativeFrequency : cycle
    const inactiveCycleLabel = inactiveCycle ? BILLING_CYCLES[inactiveCycle]?.label : null
    const inactiveTierLabel = tier ? CREATIVE_TIERS[tier]?.label : null

    const hasScheduledChange = Boolean(displaySub?.scheduledFrequency && displaySub?.scheduledPeriodEnd)
    const scheduledFrequencyLabel = hasScheduledChange
        ? (BILLING_CYCLES[displaySub.scheduledFrequency]?.label ?? displaySub.scheduledFrequency)
        : null
    const showNextPeriodBlock = hasScheduledChange && displaySub?.currentPeriodEnd !== displaySub?.scheduledPeriodEnd

    const addButtonLabel = pending ? 'Processing…' : isEnded ? 'Re-new' : 'Add'

    return (
        <div className="tw:bg-[#0F1F31] tw:rounded tw:px-6 tw:py-4 tw:flex tw:flex-col tw:gap-4">
            <div className="tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-4">
                <div className="tw:flex tw:items-center tw:gap-2 tw:min-w-0">
                    <div
                        className="tw:flex tw:items-center tw:justify-center tw:size-10 tw:rounded-full tw:shrink-0"
                        style={{ backgroundColor: planDef.iconBg }}
                    >
                        {Icon && <Icon size={24} color={planDef.iconColor} />}
                    </div>
                    <div className="tw:flex tw:flex-col tw:gap-1 tw:min-w-0">
                        <div className="tw:flex tw:items-center tw:gap-2 tw:flex-wrap">
                            <span className="tw:text-lg tw:font-semibold tw:text-white tw:leading-[1.4]">
                                {planDef.title}
                            </span>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <button
                                        type="button"
                                        className="tw:inline-flex tw:items-center tw:justify-center tw:text-[#97AFCD] tw:hover:text-white"
                                        aria-label={`${planDef.title} info`}
                                    >
                                        <InfoIcon className="tw:size-6" />
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent className="tw:max-w-xs">
                                    {planDef.title} subscription details.
                                </TooltipContent>
                            </Tooltip>
                            {(isActive || isEnded) && (
                                <StatusBadges
                                    cycleLabel={badgeCycleLabel}
                                    tierLabel={badgeTierLabel}
                                    showTier={isCreative}
                                    expired={isEnded}
                                />
                            )}
                        </div>
                        <span className="tw:text-sm tw:text-[#899CBD]">{priceParts.join(' · ')}</span>
                    </div>
                </div>
                <div className="tw:flex tw:flex-wrap tw:items-center tw:justify-end tw:gap-2 tw:shrink-0">
                    {isActive ? (
                        <>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <span>
                                        <Select
                                            value={extendFrequency}
                                            onValueChange={(v) => onExtendFrequencyChange?.(planDef.id, v)}
                                            disabled={showNextPeriodBlock}
                                        >
                                            <SelectTrigger
                                                size="sm"
                                                className="tw:h-8 tw:px-2.5 tw:rounded-2xl tw:bg-[#263E57] tw:border-0 tw:text-sm tw:text-white"
                                                aria-label={`${planDef.title} extend frequency`}
                                                disabled={showNextPeriodBlock}
                                            >
                                                <SelectValue placeholder="Cycle">
                                                    {BILLING_CYCLES[extendFrequency]?.label}
                                                </SelectValue>
                                            </SelectTrigger>
                                            <SelectContent>
                                                {BILLING_CYCLE_KEYS.filter(
                                                    (key) => key !== displaySub?.frequency,
                                                ).map((key) => (
                                                    <SelectItem key={key} value={key}>
                                                        {BILLING_CYCLES[key].label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </span>
                                </TooltipTrigger>
                                {showNextPeriodBlock && (
                                    <TooltipContent className="tw:max-w-xs">
                                        Upgrade will be available when the current period ends.
                                    </TooltipContent>
                                )}
                            </Tooltip>
                            {isCreative && (
                                <Select
                                    value={extendTier}
                                    onValueChange={(v) => onExtendTierChange?.(planDef.id, v)}
                                    disabled={showNextPeriodBlock}
                                >
                                    <SelectTrigger
                                        size="sm"
                                        className="tw:h-8 tw:px-2.5 tw:rounded-2xl tw:bg-[#263E57] tw:border-0 tw:text-sm tw:text-white"
                                        aria-label={`${planDef.title} extend tier`}
                                        disabled={showNextPeriodBlock}
                                    >
                                        <SelectValue placeholder="Tier">
                                            {extendTier ? CREATIVE_TIERS[extendTier]?.label : null}
                                        </SelectValue>
                                    </SelectTrigger>
                                    <SelectContent>
                                        {CREATIVE_TIER_KEYS.filter((key) => key !== plan?.tier).map((key) => (
                                            <SelectItem key={key} value={key}>
                                                {CREATIVE_TIERS[key].label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            )}
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => onExtend?.(planDef.id)}
                                disabled={
                                    showNextPeriodBlock ||
                                    extending ||
                                    !extendFrequency ||
                                    (isCreative && !extendTier)
                                }
                            >
                                {extending ? 'Processing…' : 'Upgrade'}
                            </Button>
                        </>
                    ) : (
                        <>
                            <Select
                                value={inactiveCycle}
                                onValueChange={(v) =>
                                    isCreative ? onCreativeFrequencyChange?.(v) : onCycleChange?.(planDef.id, v)
                                }
                            >
                                <SelectTrigger
                                    size="sm"
                                    className="tw:h-8 tw:px-2.5 tw:rounded-2xl tw:bg-[#263E57] tw:border-0 tw:text-sm tw:text-white"
                                    aria-label={`${planDef.title} billing cycle`}
                                >
                                    <SelectValue placeholder="Cycle">{inactiveCycleLabel}</SelectValue>
                                </SelectTrigger>
                                <SelectContent>
                                    {BILLING_CYCLE_KEYS.map((key) => (
                                        <SelectItem key={key} value={key}>
                                            {BILLING_CYCLES[key].label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {isCreative && (
                                <Select value={tier} onValueChange={(v) => onTierChange?.(v)}>
                                    <SelectTrigger
                                        size="sm"
                                        className="tw:h-8 tw:px-2.5 tw:rounded-2xl tw:bg-[#263E57] tw:border-0 tw:text-sm tw:text-white"
                                        aria-label="Creative tier"
                                    >
                                        <SelectValue placeholder="Tier">{inactiveTierLabel}</SelectValue>
                                    </SelectTrigger>
                                    <SelectContent>
                                        {CREATIVE_TIER_KEYS.map((key) => (
                                            <SelectItem key={key} value={key}>
                                                {CREATIVE_TIERS[key].label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            )}
                            {isUnsettled ? (
                                <>
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        onClick={() => onRetryPayment?.(planDef.id)}
                                        disabled={retrying}
                                    >
                                        {retrying ? 'Processing…' : 'Complete payment'}
                                    </Button>
                                    {bankActionUrl && (
                                        <a
                                            href={bankActionUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="tw:text-sm tw:underline tw:text-[#97AFCD] hover:tw:text-white"
                                        >
                                            Confirm payment with your bank
                                        </a>
                                    )}
                                </>
                            ) : (
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => onAdd?.(planDef.id)}
                                    disabled={pending}
                                >
                                    {addButtonLabel}
                                </Button>
                            )}
                        </>
                    )}
                </div>
            </div>
            {displaySub && (
                <div
                    className={
                        isActive
                            ? 'tw:grid tw:grid-cols-1 tw:sm:grid-cols-3 tw:gap-4'
                            : 'tw:grid tw:grid-cols-1 tw:sm:grid-cols-2 tw:gap-4'
                    }
                >
                    <StatCell
                        label="Member since"
                        value={formatDate(displaySub.createdAt)}
                        footer={monthsSince(displaySub.createdAt)}
                    />
                    {showNextPeriodBlock ? (
                        <StatCell
                            value={
                                <span className="tw:grid tw:grid-cols-2 tw:grid-rows-[auto_auto_auto] tw:gap-x-4 tw:gap-y-1 tw:w-full">
                                    <span className="tw:text-sm tw:font-normal tw:text-[#899CBD] tw:min-w-0">
                                        Current period
                                    </span>
                                    <span className="tw:flex tw:items-center tw:gap-1 tw:text-sm tw:font-normal tw:text-[#899CBD] tw:min-w-0">
                                        Next period
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <button
                                                    type="button"
                                                    className="tw:inline-flex tw:items-center tw:justify-center tw:text-[#97AFCD] tw:hover:text-white"
                                                    aria-label="Next period info"
                                                >
                                                    <InfoIcon className="tw:size-6" />
                                                </button>
                                            </TooltipTrigger>
                                            <TooltipContent className="tw:max-w-xs">
                                                The next period will start automatically after the current period ends.
                                            </TooltipContent>
                                        </Tooltip>
                                    </span>
                                    <span className="tw:min-w-0">{`${formatDate(displaySub.currentPeriodStart)} — ${formatDate(displaySub.currentPeriodEnd)}`}</span>
                                    <span className="tw:min-w-0">{`${formatDate(displaySub.currentPeriodEnd)} — ${formatDate(displaySub.scheduledPeriodEnd)}`}</span>
                                    <span className="tw:text-sm tw:font-normal tw:text-[#899CBD] tw:min-w-0">
                                        {daysUntil(displaySub.currentPeriodEnd)}
                                    </span>
                                    <span className="tw:text-sm tw:font-normal tw:text-[#899CBD] tw:min-w-0">
                                        {scheduledFrequencyLabel ? `Frequency: ${scheduledFrequencyLabel}` : ''}
                                    </span>
                                </span>
                            }
                        />
                    ) : (
                        <StatCell
                            label="Current period"
                            value={`${formatDate(displaySub.currentPeriodStart)} — ${formatDate(displaySub.currentPeriodEnd)}`}
                            footer={daysUntil(displaySub.currentPeriodEnd)}
                        />
                    )}
                    {isActive && (
                        <NextPaymentStatBlock
                            subscription={subscription}
                            onAutoRenewChange={onAutoRenewChange}
                            autoRenewPending={autoRenewPending}
                            showNextPeriod={showNextPeriodBlock}
                        />
                    )}
                </div>
            )}
            {isCreative && quota && (
                <CreativesUsageRow
                    used={quota.used}
                    purchased={isActive ? quota.purchased : (plan?.creativesQuantity ?? quota.purchased)}
                    isLegacy={!!quota.isLegacy}
                />
            )}
        </div>
    )
}

export default CurrentPlanCard
